import { restockVerdict } from '@brickloop/core/price-trend';
import { ITEM_TYPES, itemTypeLetter, type ItemType } from '@brickloop/core/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  api,
  qs,
  type ItemDetailData,
  type ItemInventoryData,
  type ItemMovementsData,
  type ItemProfitabilityData,
  type ItemTransactionsData,
} from '../api';
import { canWrite, useAuth } from '../auth';
import { statusLabel, statusTone, TYPE_LABEL } from '../lib/labels';
import { Badge, Button, Card, Empty, ErrorMessage, Notice, Pagination, Spinner, Stat, cn, money, number, unitMoney, when } from '../ui';
import { ColorSwatch } from './ItemImage';
import { PriceHistoryChart, type PriceHistoryResponse, type PriceHistorySeries } from './PriceHistoryChart';

type Item = ItemDetailData['item'];
const COND = { N: 'New', U: 'Used' } as const;

function Loading<T>({ q, children }: { q: { isPending: boolean; error: unknown; data?: T }; children: (d: T) => ReactNode }) {
  if (q.isPending) return <Spinner />;
  if (q.error || q.data === undefined) return <ErrorMessage error={q.error} />;
  return <>{children(q.data)}</>;
}

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <dt className="text-slate-500">{label}</dt>
    <dd className="text-right text-slate-900">{children ?? <span className="text-slate-400">—</span>}</dd>
  </div>
);
const blank = <span className="text-slate-400">—</span>;

/** BrickLink's own page for an item, for checking what it says. */
const CHANNEL_NAME: Record<string, string> = { bricklink: 'BrickLink', brickowl: 'Brick Owl' };

export const bricklinkPage = (type: Item['itemType'], no: string) =>
  `https://www.bricklink.com/v2/catalog/catalogitem.page?${itemTypeLetter(type)}=${encodeURIComponent(no)}`;

// ---------------------------------------------------------------- Details

export function DetailsTab({ item, onChanged }: { item: Item; onChanged: () => void }) {
  const refresh = useMutation({
    mutationFn: () => api<{ known: boolean }>(`/api/v1/items/${item.id}/refresh-bricklink`, { method: 'POST', body: {} }),
    onSuccess: onChanged,
  });
  const { user } = useAuth();
  const [newType, setNewType] = useState<ItemType>('MINIFIG');
  const qc = useQueryClient();
  const changeType = useMutation({
    mutationFn: () => api(`/api/v1/items/${item.id}/type`, { method: 'POST', body: { itemType: newType } }),
    onSuccess: () => {
      for (const k of ['item', 'items', 'lots', 'lot', 'item-inventory', 'market']) void qc.invalidateQueries({ queryKey: [k] });
      onChanged();
    },
  });
  const basic = !item.detailsFetchedAt;
  const dims = [item.dimX, item.dimY, item.dimZ].map((d) => (d === null ? null : Number(d)));
  const hasDims = dims.some((d) => d !== null);
  return (
    <div className="space-y-4">
      {basic ? (
        <Notice tone="amber">
          This is a basic record: a name and a category, made by an order or an import. BrickLink has not been asked for the rest.{' '}
          <Button small variant="secondary" disabled={refresh.isPending} onClick={() => refresh.mutate()} className="ml-2">
            {refresh.isPending ? 'Asking BrickLink…' : 'Get the details from BrickLink'}
          </Button>
        </Notice>
      ) : item.bricklinkKnown === false ? (
        <Notice>BrickLink does not know this item number, so these details are what you or the order gave.</Notice>
      ) : null}
      <ErrorMessage error={refresh.error} />
      {basic && canWrite(user) && (
        <Notice>
          <div className="flex flex-wrap items-center gap-2">
            <span>Wrong type? A {TYPE_LABEL[item.itemType].toLowerCase()} gets no price guide if BrickLink knows it as something else. Change it to</span>
            <select aria-label="New type" className="rounded border border-slate-300 px-2 py-1 text-sm" value={newType} onChange={(e) => setNewType(e.target.value as ItemType)}>
              {ITEM_TYPES.filter((t) => t !== item.itemType).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </select>
            <Button small variant="secondary" disabled={changeType.isPending} onClick={() => changeType.mutate()}>
              {changeType.isPending ? 'Changing…' : 'Change type'}
            </Button>
          </div>
          <ErrorMessage error={changeType.error} />
        </Notice>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Identification">
          <dl className="divide-y divide-slate-100">
            <Fact label="Item number">{item.itemNo}</Fact>
            <Fact label="Name">{item.name}</Fact>
            <Fact label="Type">{TYPE_LABEL[item.itemType]}</Fact>
            <Fact label="Category">{item.categoryName ?? blank}</Fact>
            <Fact label="Alternate numbers">{item.alternateNo ?? blank}</Fact>
            <Fact label="EAN / barcode">{item.ean ?? blank}</Fact>
            <Fact label="BrickLink">
              <a className="underline" href={bricklinkPage(item.itemType, item.itemNo)} target="_blank" rel="noreferrer noopener">
                Open the catalogue page
              </a>
            </Fact>
            <Fact label="Brick Owl">{item.boBoid ? `BOID ${item.boBoid}` : <span className="text-slate-500">Not matched yet (matched the first time Brick Owl prices are fetched)</span>}</Fact>
            <Fact label="eBay">
              <span className="text-slate-500">Not connected yet</span>
            </Fact>
          </dl>
        </Card>
        <Card title="Physical details">
          <dl className="divide-y divide-slate-100">
            <Fact label="Weight">{item.weightG ? `${Number(item.weightG)} g` : blank}</Fact>
            <Fact label="Dimensions (width × length × height)">{hasDims ? `${dims.map((d) => (d === null ? '?' : d)).join(' × ')}` : blank}</Fact>
            <Fact label="Year released">{item.yearReleased ?? blank}</Fact>
            <Fact label="Obsolete">{item.isObsolete === null ? blank : item.isObsolete ? <Badge tone="amber">Yes</Badge> : 'No'}</Fact>
          </dl>
          {hasDims && <p className="mt-2 text-xs text-slate-500">Sizes are as BrickLink gives them (studs for a part, centimetres for a set).</p>}
        </Card>
        <Card title="Description" className="lg:col-span-2">
          {item.description ? <p className="text-sm whitespace-pre-wrap text-slate-700">{item.description}</p> : <p className="text-sm text-slate-500">BrickLink gave no description.</p>}
        </Card>
        <Card title="Record" className="lg:col-span-2">
          <dl className="grid gap-x-8 divide-y divide-slate-100 sm:grid-cols-2 sm:divide-y-0">
            <Fact label="Made from">{item.source}</Fact>
            <Fact label="Catalogue details from BrickLink">{item.detailsFetchedAt ? when(item.detailsFetchedAt) : 'never asked'}</Fact>
            <Fact label="Created">{when(item.createdAt)}</Fact>
            <Fact label="Last changed">{when(item.updatedAt)}</Fact>
          </dl>
          {!basic && (
            <div className="mt-3">
              <Button small variant="secondary" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
                {refresh.isPending ? 'Asking BrickLink…' : 'Refresh from BrickLink'}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Inventory

export function InventoryTab({ itemId }: { itemId: number }) {
  const q = useQuery({ queryKey: ['item-inventory', itemId], queryFn: () => api<ItemInventoryData>(`/api/v1/items/${itemId}/inventory`) });
  return (
    <Loading q={q}>
      {(d) =>
        d.lots.length === 0 ? (
          <Empty>No stock of this item yet. It appears here once goods are received or stock is added.</Empty>
        ) : (
          <Card
            title="Stock by lot"
            actions={
              <span className="text-xs text-slate-500">
                {number(d.totals.qty)} pieces · {money(d.totals.costValue)} at cost · {money(d.totals.listValue)} at your prices
              </span>
            }
          >
            <div className="overflow-x-auto">
              <table className="data w-full min-w-[48rem] text-sm">
                <thead>
                  <tr>
                    <th>Colour</th>
                    <th>Cond.</th>
                    <th>Status</th>
                    <th>Listed on</th>
                    <th className="text-right">On hand</th>
                    <th>Bins</th>
                    <th className="text-right">Cost each</th>
                    <th className="text-right">Price</th>
                    <th className="text-right">Value at cost</th>
                  </tr>
                </thead>
                <tbody>
                  {d.lots.map((l) => (
                    <tr key={l.lotId} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap">
                        <Link to={`/lots/${l.lotId}`} className="hover:underline">
                          <ColorSwatch rgb={l.colorRgb} name={l.colorName} />
                        </Link>
                      </td>
                      <td>{COND[l.condition]}</td>
                      <td>
                        <Badge tone={l.status === 'live' ? 'green' : l.status === 'sold_out' ? 'slate' : 'blue'}>{l.status.replace('_', ' ')}</Badge>
                        {l.bricklinkLotId && <div className="mt-0.5 text-xs text-slate-500" title="BrickLink's own ID for this lot">BrickLink lot {l.bricklinkLotId}</div>}
                      </td>
                      <td>
                        {l.listings.length === 0 ? (
                          <span className="text-xs text-slate-400">Not listed</span>
                        ) : (
                          <div className="space-y-0.5">
                            {l.listings.map((x, i) => (
                              <div key={i} className="whitespace-nowrap text-xs" title={x.state === 'drift' ? 'The marketplace differs from BrickLoop for this lot' : undefined}>
                                <Badge tone={x.state === 'drift' ? 'amber' : x.state === 'matched' ? 'green' : 'slate'}>{CHANNEL_NAME[x.channel] ?? x.channel}</Badge>{' '}
                                <span className="tabular-nums">
                                  {number(x.qty)} @ {x.price ? unitMoney(x.price) : '—'}
                                </span>
                                {x.status !== 'Y' && <span className="text-slate-500"> · {x.status === 'S' ? 'stockroom' : 'not for sale'}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="text-right tabular-nums">{number(l.qty)}</td>
                      <td>{l.locations.length ? l.locations.map((x) => `${x.code ?? 'unplaced'} ×${number(x.qty)}`).join(', ') : blank}</td>
                      <td className="text-right tabular-nums">{l.avgCost ? unitMoney(l.avgCost) : blank}</td>
                      <td className="text-right tabular-nums">{l.price ? unitMoney(l.price) : blank}</td>
                      <td className="text-right tabular-nums">{l.avgCost ? money((l.qty * Number(l.avgCost)).toFixed(4)) : blank}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      }
    </Loading>
  );
}

// ---------------------------------------------------------------- Transactions

export function TransactionsTab({ itemId }: { itemId: number }) {
  const q = useQuery({ queryKey: ['item-transactions', itemId], queryFn: () => api<ItemTransactionsData>(`/api/v1/items/${itemId}/transactions`) });
  return (
    <Loading q={q}>
      {(d) => (
        <div className="space-y-4">
          <Card title="Purchases">
            {d.purchases.length === 0 ? (
              <p className="text-sm text-slate-500">This item has not been bought or ordered yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="data w-full min-w-[52rem] text-sm">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Purchase</th>
                      <th>Seller</th>
                      <th>Status</th>
                      <th>Colour</th>
                      <th>Cond.</th>
                      <th className="text-right">Qty</th>
                      <th className="text-right">Left</th>
                      <th className="text-right">Cost each</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.purchases.map((p, i) => (
                      <tr key={`${p.kind}-${p.purchaseId}-${i}`} className="hover:bg-slate-50">
                        <td className="whitespace-nowrap">{p.date}</td>
                        <td>
                          <Link to={`/purchases/${p.purchaseId}`} className="font-medium hover:underline">
                            {p.description || `Purchase ${p.purchaseId}`}
                          </Link>
                          <div className="text-xs text-slate-500">{p.kind === 'received' ? (p.batch ?? 'Stock added by hand') : 'Still expected'}</div>
                        </td>
                        <td>{p.sellerName ?? blank}</td>
                        <td>
                          <Badge tone={statusTone(p.status)}>{p.kind === 'on_order' ? 'On order' : statusLabel(p.status)}</Badge>
                        </td>
                        <td>{p.colorName}</td>
                        <td>{COND[p.condition]}</td>
                        <td className="text-right tabular-nums">{number(p.qty)}</td>
                        <td className="text-right tabular-nums">{p.qtyRemaining === null ? blank : number(p.qtyRemaining)}</td>
                        <td className="text-right tabular-nums">{unitMoney(p.unitCost, p.kind === 'on_order' ? p.currency : undefined)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          <Card title="Sales">
            {d.sales.length === 0 ? (
              <p className="text-sm text-slate-500">No sales yet. Orders from customers arrive in a later phase; when they do, each sale of this item is listed here.</p>
            ) : (
              <table className="data w-full text-sm">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Colour</th>
                    <th>Cond.</th>
                    <th className="text-right">Qty</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {d.sales.map((s, i) => (
                    <tr key={i}>
                      <td>{when(s.at)}</td>
                      <td>{s.colorName}</td>
                      <td>{COND[s.condition]}</td>
                      <td className="text-right tabular-nums">{number(s.qty)}</td>
                      <td>{s.refType ? `${s.refType} ${s.refId ?? ''}` : s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}
    </Loading>
  );
}

// ---------------------------------------------------------------- Stock history

const REASON: Record<string, string> = {
  purchase: 'Received',
  import: 'Imported',
  adjust: 'Adjusted',
  found: 'Found',
  lost: 'Lost',
  damaged: 'Damaged',
  order: 'Sold',
  cancel: 'Put back',
  partout: 'Part-out',
  short_pick: 'Short pick',
};

export function HistoryTab({ itemId }: { itemId: number }) {
  const [page, setPage] = useState(1);
  const q = useQuery({ queryKey: ['item-movements', itemId, page], queryFn: () => api<ItemMovementsData>(`/api/v1/items/${itemId}/movements${qs({ page })}`) });
  return (
    <Loading q={q}>
      {(d) =>
        d.rows.length === 0 ? (
          <Empty>No stock has ever moved for this item.</Empty>
        ) : (
          <Card title="Every change in quantity, newest first">
            <div className="overflow-x-auto">
              <table className="data w-full min-w-[44rem] text-sm">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Colour</th>
                    <th>Cond.</th>
                    <th>What</th>
                    <th className="text-right">Change</th>
                    <th>Note</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((m) => (
                    <tr key={m.id}>
                      <td className="whitespace-nowrap">{when(m.at)}</td>
                      <td>{m.colorName}</td>
                      <td>{COND[m.condition]}</td>
                      <td>{REASON[m.reason] ?? m.reason}</td>
                      <td className={`text-right tabular-nums ${m.delta < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{m.delta > 0 ? `+${m.delta}` : m.delta}</td>
                      <td>{m.note ?? blank}</td>
                      <td>{m.by ?? blank}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={d.pageSize} total={d.total} onPage={setPage} />
          </Card>
        )
      }
    </Loading>
  );
}

// ---------------------------------------------------------------- Price history

const TREND_TONE = { rising: 'green', falling: 'red', flat: 'slate', too_little: 'slate' } as const;
const TREND_LABEL = { rising: 'Rising', falling: 'Falling', flat: 'Flat', too_little: 'Not enough history' } as const;
const VERDICT_TONE = { good: 'text-emerald-700', neutral: 'text-slate-600', bad: 'text-red-700' } as const;

/** The guide the series came from, in the same words the Market tab uses. */
const GUIDE_LABEL = { sold: 'Sold, 6 months', stock: 'For sale now' } as const;

function PriceHistoryCard({ series }: { series: PriceHistorySeries }) {
  const trend = series.trend;
  const ccy = trend.currency ?? series.series.find((p) => p.currency)?.currency ?? 'GBP';
  const verdict = restockVerdict(trend);
  const first = trend.earliestAvg;
  const last = trend.latestAvg;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          {series.colorRgb && <ColorSwatch rgb={series.colorRgb} name="" />}
          <span>
            {series.colorName} · {COND[series.condition]} · {GUIDE_LABEL[series.guideType]}
          </span>
        </span>
      }
      actions={<Badge tone={TREND_TONE[trend.state]}>{TREND_LABEL[trend.state]}</Badge>}
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-700">{trend.summary}</p>

        <PriceHistoryChart points={series.series} currency={ccy} />

        <dl className="flex flex-wrap gap-x-8 gap-y-1 text-sm">
          <div className="flex gap-2">
            <dt className="text-slate-500">First seen</dt>
            <dd className="font-medium tabular-nums">{unitMoney(first, ccy)}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-slate-500">Latest</dt>
            <dd className="font-medium tabular-nums">{unitMoney(last, ccy)}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-slate-500">Change</dt>
            <dd className="font-medium tabular-nums">
              {trend.change === null ? (
                <span className="text-slate-400">—</span>
              ) : (
                <span className={trend.change > 0 ? 'text-emerald-700' : trend.change < 0 ? 'text-red-700' : undefined}>
                  {trend.change > 0 ? '+' : ''}
                  {unitMoney(trend.change, ccy)}
                  {trend.changePct !== null ? ` (${trend.changePct > 0 ? '+' : ''}${Math.round(trend.changePct * 10) / 10}%)` : ''}
                </span>
              )}
            </dd>
          </div>
        </dl>

        <p className={cn('text-sm font-medium', VERDICT_TONE[verdict.tone])}>{verdict.text}</p>
      </div>
    </Card>
  );
}

export function PriceHistoryTab({ itemId }: { itemId: number }) {
  const q = useQuery({ queryKey: ['item-price-history', itemId], queryFn: () => api<PriceHistoryResponse>(`/api/v1/items/${itemId}/price-history`) });
  return (
    <Loading q={q}>
      {(d) =>
        d.series.length === 0 ? (
          <Empty>
            No price history held for this item yet. Figures are collected overnight for parts in stock, and sold-out parts are
            watched too, so a price starts being recorded as soon as the part is known.
          </Empty>
        ) : (
          <div className="space-y-4">
            {d.series.map((s) => (
              <PriceHistoryCard key={`${s.colorId}-${s.condition}-${s.guideType}`} series={s} />
            ))}
          </div>
        )
      }
    </Loading>
  );
}

// ---------------------------------------------------------------- System notes

export function NotesTab({ itemId }: { itemId: number }) {
  const qc = useQueryClient();
  void qc;
  const q = useQuery({ queryKey: ['item-notes', itemId], queryFn: () => api<{ notes: { at: string; action: string; by: string | null; data: unknown }[] }>(`/api/v1/items/${itemId}/notes`) });
  return (
    <Loading q={q}>
      {({ notes }) =>
        notes.length === 0 ? (
          <Empty>Nothing recorded against this item yet.</Empty>
        ) : (
          <Card title="System notes">
            <ul className="divide-y divide-slate-100 text-sm">
              {notes.map((n, i) => (
                <li key={i} className="py-2">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-medium">{n.action.replace('.', ' · ').replace(/_/g, ' ')}</span>
                    <span className="text-xs text-slate-500">
                      {when(n.at)}
                      {n.by ? ` · ${n.by}` : ' · BrickLoop'}
                    </span>
                  </div>
                  {n.data !== null && <div className="mt-0.5 font-mono text-xs break-words text-slate-500">{JSON.stringify(n.data)}</div>}
                </li>
              ))}
            </ul>
          </Card>
        )
      }
    </Loading>
  );
}

// ---------------------------------------------------------------- Profitability

const profitTone = (v: string | null) => (v === null ? '' : Number(v) < 0 ? 'text-red-700' : 'text-emerald-700');

/**
 * Stock bought in different places costs different amounts but sells at one price, so cost is averaged over the
 * pieces in stock (each purchase counting by how many are left) and profit is the price less that average.
 */
export function ProfitabilityTab({ itemId }: { itemId: number }) {
  const q = useQuery({ queryKey: ['item-profitability', itemId], queryFn: () => api<ItemProfitabilityData>(`/api/v1/items/${itemId}/profitability`) });
  const [open, setOpen] = useState<number | null>(null);
  return (
    <Loading q={q}>
      {(d) =>
        d.lots.length === 0 ? (
          <Empty>No stock of this item, so there is nothing to work a profit out for. It appears once goods are received.</Empty>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <Stat label="Average cost" value={d.total.avgCost ? unitMoney(d.total.avgCost) : '—'} note="Per piece, over what is in stock" />
              <Stat label="Average price" value={d.total.avgPrice ? unitMoney(d.total.avgPrice) : '—'} note="Per piece, over priced stock" />
              <Stat label="Profit each" value={<span className={profitTone(d.total.profitEach)}>{d.total.profitEach ? unitMoney(d.total.profitEach) : '—'}</span>} note="Price less average cost" />
              <Stat label="Margin" value={<span className={profitTone(d.total.profitEach)}>{d.total.marginPct === null ? '—' : `${d.total.marginPct}%`}</span>} note="Profit as a share of price" />
              <Stat label="If it all sold" value={<span className={profitTone(d.total.profitTotal)}>{d.total.profitTotal ? money(d.total.profitTotal) : '—'}</span>} note="At your prices, before fees" />
            </div>
            {d.total.unpricedQty > 0 && <Notice tone="amber">{number(d.total.unpricedQty)} pieces have no price set, so they are left out of the price and profit figures.</Notice>}

            <Card title="By lot" actions={<span className="text-xs text-slate-500">Press a row to see the purchases behind its average</span>}>
              <div className="overflow-x-auto">
                <table className="data w-full min-w-[48rem] text-sm">
                  <thead>
                    <tr>
                      <th>Colour</th>
                      <th>Cond.</th>
                      <th className="text-right">In stock</th>
                      <th className="text-right">Average cost</th>
                      <th className="text-right">Price</th>
                      <th className="text-right">Profit each</th>
                      <th className="text-right">Margin</th>
                      <th className="text-right">If all sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.lots.map((l) => (
                      <Fragment key={l.lotId}>
                        <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(open === l.lotId ? null : l.lotId)} aria-expanded={open === l.lotId}>
                          <td className="whitespace-nowrap">
                            <span className="mr-1 text-slate-400">{open === l.lotId ? '▾' : '▸'}</span>
                            <ColorSwatch rgb={l.colorRgb} name={l.colorName} />
                          </td>
                          <td>{COND[l.condition]}</td>
                          <td className="text-right tabular-nums">{number(l.qty)}</td>
                          <td className="text-right tabular-nums">{l.avgCost ? unitMoney(l.avgCost) : blank}</td>
                          <td className="text-right tabular-nums">{l.price ? unitMoney(l.price) : <span className="text-amber-700">not set</span>}</td>
                          <td className={`text-right tabular-nums ${profitTone(l.profitEach)}`}>{l.profitEach ? unitMoney(l.profitEach) : blank}</td>
                          <td className={`text-right tabular-nums ${profitTone(l.profitEach)}`}>{l.marginPct === null ? '—' : `${l.marginPct}%`}</td>
                          <td className={`text-right tabular-nums ${profitTone(l.profitTotal)}`}>{l.profitTotal ? money(l.profitTotal) : blank}</td>
                        </tr>
                        {open === l.lotId && (
                          <tr>
                            <td colSpan={8} className="bg-slate-50">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-left text-slate-500">
                                    <th className="py-1 font-medium">Bought</th>
                                    <th className="py-1 font-medium">Purchase</th>
                                    <th className="py-1 text-right font-medium">Left</th>
                                    <th className="py-1 text-right font-medium">Cost each</th>
                                    <th className="py-1 text-right font-medium">Profit each at the price</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {l.layers.map((y) => {
                                    const each = l.price && !y.costUnknown ? (Number(l.price) - Number(y.unitCost)).toFixed(4) : null;
                                    return (
                                      <tr key={y.entryId}>
                                        <td className="py-1">{y.date}</td>
                                        <td className="py-1">
                                          <Link to={`/purchases/${y.purchaseId}`} className="hover:underline">
                                            {y.purchase || `Purchase ${y.purchaseId}`}
                                          </Link>{' '}
                                          <span className="text-slate-500">{y.batch}</span>
                                        </td>
                                        <td className="py-1 text-right tabular-nums">{number(y.qty)}</td>
                                        <td className="py-1 text-right tabular-nums">{y.costUnknown ? 'unknown' : Number(y.unitCost) > 0 ? unitMoney(y.unitCost) : 'free'}</td>
                                        <td className={`py-1 text-right tabular-nums ${profitTone(each)}`}>{each ? unitMoney(each) : '—'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {d.colours.length > 1 && (
              <Card title="By colour (new and used together)">
                <table className="data w-full text-sm">
                  <thead>
                    <tr>
                      <th>Colour</th>
                      <th className="text-right">In stock</th>
                      <th className="text-right">Average cost</th>
                      <th className="text-right">Average price</th>
                      <th className="text-right">Profit each</th>
                      <th className="text-right">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.colours.map((c) => (
                      <tr key={c.colorId}>
                        <td>{c.colorName}</td>
                        <td className="text-right tabular-nums">{number(c.qty)}</td>
                        <td className="text-right tabular-nums">{c.avgCost ? unitMoney(c.avgCost) : blank}</td>
                        <td className="text-right tabular-nums">{c.avgPrice ? unitMoney(c.avgPrice) : blank}</td>
                        <td className={`text-right tabular-nums ${profitTone(c.profitEach)}`}>{c.profitEach ? unitMoney(c.profitEach) : blank}</td>
                        <td className={`text-right tabular-nums ${profitTone(c.profitEach)}`}>{c.marginPct === null ? '—' : `${c.marginPct}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>
        )
      }
    </Loading>
  );
}
